// UserContext.js
import React, { useState, useContext, useEffect, useMemo } from "react";
import { createContext, useContextSelector } from "use-context-selector";
import userAxios from "../../api/axiosInstance";
import axios from "axios";
import { backendUrl } from "../../api/constants";
import { enqueueSnackbar } from "notistack";

const UserContext = createContext(null);

// Hook for only user data and setters (stable, won't cause re-renders)
export const useUserSetters = () => {
  const user = useContextSelector(UserContext, state => state.user);
  const setUser = useContextSelector(UserContext, state => state.setUser);
  const logout = useContextSelector(UserContext, state => state.logout);
  const fetchUser = useContextSelector(UserContext, state => state.fetchUser);
  const setCalendarAccounts = useContextSelector(UserContext, state => state.setCalendarAccounts);
  const fetchCalendarAccounts = useContextSelector(UserContext, state => state.fetchCalendarAccounts);
 
  return {
    user,
    setUser,
    logout,
    fetchUser,
    setCalendarAccounts,
    fetchCalendarAccounts,
  };
};

// Combined hook for backward compatibility
export const useUser = () => {
  const context = useContextSelector(UserContext, state => state);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState();
  const [calendarAccounts,setCalendarAccounts] = useState([]); 
  const [isLoading, setIsLoading] = useState(true);

  const authenticateUser = async () => {
    // Don't set loading if already loading or user exists
    if (!isLoading && !user) {
      setIsLoading(true);
    }
    
    const token = localStorage.getItem("jwt_token") || sessionStorage.getItem("jwt_token");

    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const { data } = await axios.get(`${backendUrl}/user/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUser(data.user);
    } catch (error) {
      console.error("Authentication error: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUser = async (opts) => {
    // Only set loading if not already loading and not suppressing
    if (!opts?.supressLoading && !isLoading) {
      setIsLoading(true);
    }
    
    try {
      const { data } = await userAxios.get("/user/me");
      if (data.error) {
        console.log(data.error);
        return null;
      } else {
        setUser(data.user);
        return data.user;
      }
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      if (!opts?.supressLoading) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    console.log('isLoading',isLoading);
    console.log('user',user);
  },[isLoading,user]);

  const fetchCalendarAccounts = async () => {
    try {
      if(!user || user?.accountType === 'user'){
        return setCalendarAccounts([]);
      }
      
      // Only fetch if calendar accounts haven't been loaded yet
      if (calendarAccounts.length === 0) {
        const { data } = await userAxios.get(`/user/calendar/google/accounts`);
        if (data.error) {
          return enqueueSnackbar(data.message, { variant: "error" });
        }
        setCalendarAccounts(data.accounts);
        return data.accounts;
      }
    } catch (error) {
      console.log(error.response.data);
    }
  };

  useEffect(() => {
    authenticateUser();
  }, []);

  useEffect(() => {
    // console.log("User changed: ", user);
    if (user && calendarAccounts.length === 0) {
      fetchCalendarAccounts();
    }
  }, [user]);

  const logout = async () => {
    sessionStorage.removeItem("jwt_token");
    localStorage.removeItem("jwt_token");
    setUser(null);
  };

  const contextValue = useMemo(() => ({
    user,
    setUser,
    calendarAccounts,
    setCalendarAccounts,
    fetchCalendarAccounts,
    logout,
    authenticateUser,
    isLoading,
    fetchUser,
  }), [
    user,
    setUser,
    calendarAccounts,
    setCalendarAccounts,
    fetchCalendarAccounts,
    logout,
    authenticateUser,
    isLoading,
    fetchUser,
  ]);

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};
